import confetti from 'canvas-confetti';
import { useEffect, useState } from 'react';
import { RuleError, matchStatus, playerStats, seatsOfTeam, totals, type TeamId } from '../game/match';
import { GuaaDetailsSheet } from '../components/GuaaDetails';
import { LosersCard, ResultHero } from '../components/ResultHero';
import { HistoryTable } from '../components/Schedule';
import { Scoreboard } from '../components/Scoreboard';
import { Spinner } from '../components/ui';
import { NetworkError } from '../lib/api';
import { Link, navigate } from '../lib/router';
import { continueMatch, createMatch, toMatchState } from '../lib/store';
import { toast } from '../lib/toast';
import { shareOrCopy } from './Dashboard';
import { BackButton, NotFound, SyncBadge, useMatchData } from './shared';

const REASON: Record<string, string> = {
  closed: 'when you finished the match',
  decidedEarly: "early: the other team couldn't catch up",
};

export function EndScreen({ code }: { code: string }) {
  const { data, error, canEdit } = useMatchData(code);
  const [busy, setBusy] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const status = data ? matchStatus(toMatchState(data)) : null;
  const result = status?.result ?? null;
  const winnerColor = data && result && result !== 'tie' ? data.match.teams[result].color : '#d4a84b';

  // A big opening burst, then cannons from both sides for a couple of seconds.
  useEffect(() => {
    if (!result) return;
    const colors = [winnerColor, '#d4a84b', '#ecd48f', '#f7f1e3'];
    void confetti({ particleCount: 140, spread: 100, origin: { y: 0.28 }, colors, disableForReducedMotion: true });
    const end = Date.now() + 2200;
    const timer = setInterval(() => {
      if (Date.now() > end) return clearInterval(timer);
      void confetti({ particleCount: 6, angle: 60, spread: 60, origin: { x: 0, y: 0.6 }, colors, disableForReducedMotion: true });
      void confetti({ particleCount: 6, angle: 120, spread: 60, origin: { x: 1, y: 0.6 }, colors, disableForReducedMotion: true });
    }, 180);
    return () => clearInterval(timer);
  }, [result, winnerColor]);

  if (error && !data) return <NotFound message={error} />;
  if (!data || !status) return <Spinner label="Loading result" />;
  const { match, deals } = data;

  if (status.phase !== 'finished') {
    return (
      <main className="mx-auto grid max-w-md place-items-center gap-4 px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold">This match is still being played.</h1>
        <Link href={`/m/${code}`} className="btn btn-primary">
          Back to the table
        </Link>
      </main>
    );
  }

  const t = totals(deals);
  const players = playerStats(toMatchState(data));
  const tie = result === 'tie';
  const winner = tie ? null : match.teams[result as TeamId];
  const winnerSeats = tie ? [] : seatsOfTeam(result as TeamId);

  const rematch = async () => {
    setBusy(true);
    try {
      const next = await createMatch({ seats: match.seats, teams: match.teams, rules: match.rules });
      navigate(`/m/${next}/wheel`);
    } catch (err) {
      toast(err instanceof NetworkError ? 'A rematch needs a connection.' : (err as Error).message, 'error');
      setBusy(false);
    }
  };

  const shareText = tie
    ? `Rang ${code}: ${match.teams.A.name} ${t.guaaA} – ${t.guaaB} ${match.teams.B.name} Guaa. It's a tie!`
    : `Rang ${code}: ${match.teams.A.name} ${t.guaaA} – ${t.guaaB} ${match.teams.B.name} Guaa. 🏆 ${winner!.name} win (${winnerSeats
        .map((s) => match.seats[s]!.name)
        .join(' & ')})!`;

  const stat = (label: string, a: number | string, b: number | string) => (
    <tr className="border-b border-ink/8 last:border-0">
      <th scope="row" className="py-2 text-left font-normal text-ink-soft">
        {label}
      </th>
      <td className="py-2 text-center font-semibold tabular-nums">{a}</td>
      <td className="py-2 text-center font-semibold tabular-nums">{b}</td>
    </tr>
  );

  return (
    <main className="mx-auto max-w-lg px-4 pb-16">
      <header className="flex items-center gap-2 pt-[max(1rem,env(safe-area-inset-top))] pb-3">
        <BackButton href={`/m/${code}`} label="Back to the table" />
        <span className="flex-1 font-mono font-bold tracking-widest">{code}</span>
        <SyncBadge code={code} />
      </header>

      <ResultHero
        result={result!}
        teams={match.teams}
        seats={match.seats}
        players={players}
        guaa={{ A: t.guaaA, B: t.guaaB }}
        subtitle={`${tie ? 'Level' : 'Decided'} ${REASON[status.reason ?? 'closed']} · ${deals.length} deals`}
        finishedAt={match.closedAt ?? match.finishedAt ?? match.updatedAt}
      />

      {tie ? (
        <section className="card mt-4 overflow-hidden" aria-label="Final score">
          <button type="button" className="block w-full p-4 text-left hover:bg-ink/5" onClick={() => setDetailsOpen(true)} aria-haspopup="dialog">
            <Scoreboard teams={match.teams} t={t} compact />
            <span className="mt-2 block text-center text-xs font-bold text-ink-soft">Who got Guaa? ›</span>
          </button>
        </section>
      ) : (
        <div className="mt-4">
          <LosersCard
            loser={result === 'A' ? 'B' : 'A'}
            teams={match.teams}
            seats={match.seats}
            players={players}
            guaa={result === 'A' ? t.guaaB : t.guaaA}
            totals={t}
            onOpenDetails={() => setDetailsOpen(true)}
          />
        </div>
      )}

      <section className="card mt-4 p-4" aria-labelledby="stats-h">
        <h2 id="stats-h" className="mb-2 font-display text-xl font-bold">
          Team stats
        </h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs tracking-wide text-ink-soft uppercase">
              <td />
              <th scope="col" className="pb-1">{match.teams.A.name}</th>
              <th scope="col" className="pb-1">{match.teams.B.name}</th>
            </tr>
          </thead>
          <tbody>
            {stat('Guaa', t.guaaA, t.guaaB)}
            {stat('Chose Rang', t.calledA, t.calledB)}
            {stat('Saved', t.savedA, t.savedB)}
            {stat('Courts against', t.courtsA, t.courtsB)}
          </tbody>
        </table>
      </section>

      <section className="card mt-4 p-4" aria-labelledby="hist-h">
        <h2 id="hist-h" className="mb-2 font-display text-xl font-bold">
          Every deal
        </h2>
        <HistoryTable deals={deals} seats={match.seats} teams={match.teams} />
      </section>

      <GuaaDetailsSheet
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        state={toMatchState(data)}
        seats={match.seats}
        teams={match.teams}
      />

      {canEdit && (
        <section className="card mt-6 p-4" aria-labelledby="more-h">
          <h2 id="more-h" className="font-display text-xl font-bold">
            Keep playing?
          </h2>
          <p className="mb-3 text-sm text-ink-soft">
            Add another round: the deck keeps rotating and Guaa keeps adding up.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {[2, 3].map((hands) => (
              <button
                key={hands}
                type="button"
                className="btn btn-ink flex-col gap-0 py-3"
                onClick={() => {
                  try {
                    continueMatch(code, hands);
                    toast(`${hands * 4} more deals added`, 'success');
                    navigate(`/m/${code}`);
                  } catch (err) {
                    toast(err instanceof RuleError ? err.message : 'Could not extend the match.', 'error');
                  }
                }}
              >
                <span className="text-lg">+{hands} hands each</span>
                <span className="text-xs font-normal text-cream/75">{hands * 4} more deals</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="mt-6 grid gap-3">
        <button type="button" className="btn btn-primary h-14 text-lg" onClick={rematch} disabled={busy}>
          {busy ? 'Setting up…' : '↻ Rematch (same players)'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => shareOrCopy(shareText, `${location.origin}/m/${code}/end`)}>
          📤 Share result
        </button>
        <Link href="/home" className="btn btn-ghost">
          Home
        </Link>
      </div>
    </main>
  );
}
